import { z } from 'zod';
import { CommunicationChannel } from './communications';
import { JourneyEventNames } from './events';
import { FilterOperator, UnitOfTime } from './filter';
import { WarehouseTypes } from './projects';
import { EventStatus } from './srtAnalytics';

export enum AudienceFilterType {
    PROPERTY = 'property',
    AUDIENCE_METRIC = 'audience_metric',
    AUDIENCE_IN_AUDIENCE = 'audience_in_audience',
    CUSTOM_METRIC = 'custom_metric',
    CUSTOM_DIMENSION = 'custom_dimension',
    EVENTS = 'events',
    CAMPAIGN_EVENTS = 'campaign_events',
    JOURNEY_EVENTS = 'journey_events',
}

export enum LogicType {
    AND = 'AND',
    OR = 'OR',
}

export enum PlanCreationStatus {
    SUCCESS = 'success',
    FAILURE = 'failure',
    PARTIAL = 'partial',
    PENDING = 'pending',
}

export const propertyFilterSchema = z.object({
    type: z.literal(AudienceFilterType.PROPERTY),
    fieldId: z
        .string()
        .describe(
            'CRITICAL: "field id" of the column. MUST ALWAYS be in the format table_column (underscore). Examples: users_email, orders_amount, events_timestamp. Never use dots (.) in fieldId. Use EXACT case as provided in available_data.',
        )
        .refine(
            (val) => val.includes('_') && !val.includes('.'),
            'fieldId must contain underscore (_) and must not contain dot (.). Format: table_column',
        ),
    fieldRef: z
        .string()
        .describe(
            'CRITICAL: column name in the format table.column (dot). Examples: users.email, orders.amount, events.timestamp. Must contain exactly one dot (.) separating table and column. Use EXACT case as provided in available_data.',
        )
        .refine((val) => {
            const dotCount = (val.match(/\./g) || []).length;
            return dotCount === 1;
        }, 'fieldRef must contain exactly one dot (.) separating table and column. Format: table.column'),
    operator: z.nativeEnum(FilterOperator),
    values: z.union([
        z.array(z.string()),
        z.array(z.number()),
        z.array(z.boolean()),
    ]),
    additionalProperties: z
        .object({
            unitOfTime: z.nativeEnum(UnitOfTime),
            completed: z.boolean(),
        })
        .optional(),
    humanReadable: z.string(),
});

export type PropertyFilter = z.infer<typeof propertyFilterSchema>;

const customMetricFilterSchema = z.object({
    type: z.literal(AudienceFilterType.CUSTOM_METRIC),
    fieldId: z.string().describe('id of the custom metric'),
    operator: z.nativeEnum(FilterOperator),
    values: z.union([
        z.array(z.string()),
        z.array(z.number()),
        z.array(z.boolean()),
    ]),
    additionalProperties: z
        .object({
            unitOfTime: z.nativeEnum(UnitOfTime),
            completed: z.boolean(),
        })
        .optional(),
    humanReadable: z.string(),
});

export type CustomMetricFilter = z.infer<typeof customMetricFilterSchema>;

const customDimensionFilterSchema = z.object({
    type: z.literal(AudienceFilterType.CUSTOM_DIMENSION),
    fieldId: z.string().describe('id of the custom dimension'),
    operator: z.nativeEnum(FilterOperator),
    values: z.union([
        z.array(z.string()),
        z.array(z.number()),
        z.array(z.boolean()),
    ]),
    additionalProperties: z
        .object({
            unitOfTime: z.nativeEnum(UnitOfTime),
            completed: z.boolean(),
        })
        .optional(),
    humanReadable: z.string(),
});

export type CustomDimensionFilter = z.infer<typeof customDimensionFilterSchema>;

const audienceMetricFilterSchema = z.object({
    type: z.literal(AudienceFilterType.AUDIENCE_METRIC),
    table: z.string(),
    operator: z.enum([
        FilterOperator.EQUALS,
        FilterOperator.NOT_EQUALS,
        FilterOperator.LESS_THAN,
        FilterOperator.GREATER_THAN,
        FilterOperator.LESS_THAN_OR_EQUAL,
        FilterOperator.GREATER_THAN_OR_EQUAL,
    ]),
    count: z.number(),
    propertyFilters: z.array(propertyFilterSchema),
    humanReadable: z.string(),
});

export type AudienceMetricFilter = z.infer<typeof audienceMetricFilterSchema>;

const audienceInAudienceFilterSchema = z.object({
    type: z.literal(AudienceFilterType.AUDIENCE_IN_AUDIENCE),
    operator: z
        .enum([FilterOperator.EQUALS, FilterOperator.NOT_EQUALS])
        .describe('equals for belong and not equals for not belong'),
    audienceId: z.string(),
    userIdOfRelation: z
        .string()
        .describe(
            "CRITICAL: field id of user id of the relation. MUST be the PRIMARY table's primary key in table_column format (underscore). Examples: users_id, customers_id. Never use dots (.) in userIdOfRelation. Do not assume field names - use the actual primary key from available_data. Use EXACT case as provided in available_data.",
        )
        .refine(
            (val) => val.includes('_') && !val.includes('.'),
            'userIdOfRelation must contain underscore (_) and must not contain dot (.). Format: table_column',
        ),
    humanReadable: z.string(),
});

export type AudienceInAudienceFilter = z.infer<
    typeof audienceInAudienceFilterSchema
>;

const eventFilterSchema = z.object({
    type: z.literal(AudienceFilterType.EVENTS),
    operator: z.enum([
        FilterOperator.EQUALS,
        FilterOperator.NOT_EQUALS,
        FilterOperator.LESS_THAN,
        FilterOperator.GREATER_THAN,
        FilterOperator.LESS_THAN_OR_EQUAL,
        FilterOperator.GREATER_THAN_OR_EQUAL,
    ]),
    eventsTableName: z.string(),
    count: z.number(),
    eventColumn: z.string(),
    eventColumnValue: z.string(),
    eventTimeStampColumn: z.string(),
    warehouseType: z.nativeEnum(WarehouseTypes),
    timeRangeOperator: z
        .nativeEnum(FilterOperator)
        .optional()
        .describe(
            'Specifies the operator for the time range. Example: "inThePast", "notInThePast", etc.',
        ),
    timeRangeValue: z
        .array(z.string())
        .optional()
        .describe(
            'Specifies the time range value. Example: A. ["7"] if condition is "inThePast 7 days" B. ["2024-01-01T00:00:00.000Z", "2024-01-31T23:59:59.999Z"] if condition is "inBetween 2024-01-01 and 2024-01-31"',
        ),
    additionalProperties: z
        .object({
            unitOfTime: z
                .nativeEnum(UnitOfTime)
                .describe(
                    'Specifies the unit of time for the time range. Example: "days", "months", etc.',
                ),
            completed: z
                .boolean()
                .describe('Whether the unit of time is completed or not.'),
        })
        .optional(),
    propertyFilters: z
        .array(propertyFilterSchema)
        .default([])
        .describe(
            'A list of property filters applied to the event. Expected to be an empty array if no property filters were applied.',
        ),
    humanReadable: z.string(),
});

export type EventFilter = z.infer<typeof eventFilterSchema>;

const campaignEventFilterSchema = z.object({
    type: z.literal(AudienceFilterType.CAMPAIGN_EVENTS),
    operator: z.enum([
        FilterOperator.EQUALS,
        FilterOperator.NOT_EQUALS,
        FilterOperator.LESS_THAN,
        FilterOperator.GREATER_THAN,
        FilterOperator.LESS_THAN_OR_EQUAL,
        FilterOperator.GREATER_THAN_OR_EQUAL,
    ]),
    eventsTableName: z.string(),
    warehouseType: z.nativeEnum(WarehouseTypes),
    count: z.number(),
    channel: z.nativeEnum(CommunicationChannel),
    event: z.nativeEnum(EventStatus),
    campaignId: z.string().optional(),
    timeRangeOperator: z
        .nativeEnum(FilterOperator)
        .optional()
        .describe(
            'Specifies the operator for the time range. Example: "inThePast", "notInThePast", etc.',
        ),
    timeRangeValue: z
        .array(z.string())
        .optional()
        .describe(
            'Specifies the time range value. Example: A. ["7"] if condition is "inThePast 7 days" B. ["2024-01-01T00:00:00.000Z", "2024-01-31T23:59:59.999Z"] if condition is "inBetween 2024-01-01 and 2024-01-31"',
        ),
    additionalProperties: z
        .object({
            unitOfTime: z
                .nativeEnum(UnitOfTime)
                .describe(
                    'Specifies the unit of time for the time range. Example: "days", "months", etc.',
                ),
            completed: z
                .boolean()
                .describe('Whether the unit of time is completed or not.'),
        })
        .optional(),
    humanReadable: z.string(),
});

export type CampaignEventFilter = z.infer<typeof campaignEventFilterSchema>;

const journeyEventFilterSchema = z.object({
    type: z.literal(AudienceFilterType.JOURNEY_EVENTS),
    operator: z.enum([
        FilterOperator.EQUALS,
        FilterOperator.NOT_EQUALS,
        FilterOperator.LESS_THAN,
        FilterOperator.GREATER_THAN,
        FilterOperator.LESS_THAN_OR_EQUAL,
        FilterOperator.GREATER_THAN_OR_EQUAL,
    ]),
    journeyEventsTableName: z.string(),
    warehouseType: z.nativeEnum(WarehouseTypes),
    count: z.number(),
    event: z.enum([
        JourneyEventNames.JOURNEY_ENTERED,
        JourneyEventNames.JOURNEY_EXITED,
        JourneyEventNames.JOURNEY_CONVERTED,
    ]),
    journeyId: z.string().optional(),
    timeRangeOperator: z
        .nativeEnum(FilterOperator)
        .optional()
        .describe(
            'Specifies the operator for the time range. Example: "inThePast", "notInThePast", etc.',
        ),
    timeRangeValue: z
        .array(z.string())
        .optional()
        .describe(
            'Specifies the time range value. Example: A. ["7"] if condition is "inThePast 7 days" B. ["2024-01-01T00:00:00.000Z", "2024-01-31T23:59:59.999Z"] if condition is "inBetween 2024-01-01 and 2024-01-31"',
        ),
    additionalProperties: z
        .object({
            unitOfTime: z
                .nativeEnum(UnitOfTime)
                .describe(
                    'Specifies the unit of time for the time range. Example: "days", "months", etc.',
                ),
            completed: z
                .boolean()
                .describe('Whether the unit of time is completed or not.'),
        })
        .optional(),
    humanReadable: z.string(),
});

export type JourneyEventFilter = z.infer<typeof journeyEventFilterSchema>;

const visualDimensionFiltersSchema = z.object({
    logicType: z.nativeEnum(LogicType).default(LogicType.AND),
    propertyFilters: z
        .array(propertyFilterSchema)
        .default([])
        .describe(
            'A list of property filters applied to the audience. Expected to be an empty array if no property filters were applied.',
        ),
    audienceInAudienceFilters: z
        .array(audienceInAudienceFilterSchema)
        .default([])
        .describe(
            'A list of audience in audience filters applied to the audience. Expected to be an empty array if no audience in audience filters were applied.',
        ),
    customDimensionFilters: z
        .array(customDimensionFilterSchema)
        .default([])
        .describe(
            'A list of custom dimension filters applied to the audience. Expected to be an empty array if no custom dimension filters were applied.',
        ),
});

const visualMetricFiltersSchema = z.object({
    logicType: z.nativeEnum(LogicType).default(LogicType.AND),
    audienceMetricFilters: z
        .array(audienceMetricFilterSchema)
        .default([])
        .describe(
            'A list of audience metric filters applied to the audience. Expected to be an empty array if no audience metric filters were applied.',
        ),
    customMetricFilters: z
        .array(customMetricFilterSchema)
        .default([])
        .describe(
            'A list of custom metric filters applied to the audience. Expected to be an empty array if no custom metric filters were applied.',
        ),
    campaignEventFilters: z
        .array(campaignEventFilterSchema)
        .default([])
        .describe(
            'A list of campaign event filters applied to the audience. Expected to be an empty array if no campaign event filters were applied.',
        ),
    eventFilters: z
        .array(eventFilterSchema)
        .default([])
        .describe(
            'A list of event filters applied to the audience. Expected to be an empty array if no event filters were applied.',
        ),
    journeyEventFilters: z
        .array(journeyEventFilterSchema)
        .default([])
        .describe(
            'A list of journey event filters applied to the audience. Expected to be an empty array if no journey event filters were applied.',
        ),
});

export type VisualDimensionFilters = z.infer<
    typeof visualDimensionFiltersSchema
>;
export type VisualMetricFilters = z.infer<typeof visualMetricFiltersSchema>;

const filterGroupSchema = z.object({
    visualDimensionFilters: visualDimensionFiltersSchema.default({
        logicType: LogicType.AND,
        propertyFilters: [],
        audienceInAudienceFilters: [],
        customDimensionFilters: [],
    }),
    visualMetricFilters: visualMetricFiltersSchema.default({
        logicType: LogicType.AND,
        audienceMetricFilters: [],
        customMetricFilters: [],
        campaignEventFilters: [],
        eventFilters: [],
        journeyEventFilters: [],
    }),
});

export type AudienceFilterGroup = z.infer<typeof filterGroupSchema>;

export const audiencePlannerResponseSchema = z.object({
    logicType: z.nativeEnum(LogicType).default(LogicType.AND),
    groups: z
        .array(filterGroupSchema)
        .default([])
        .describe(
            'A list of filter groups applied to the audience. Expected to be an empty array if no filters were applied.',
        ),
    supervisorResponse: z.string().min(1, 'supervisorResponse cannot be empty'),
    requiredCustomAttributes: z
        .array(
            z.object({
                name: z
                    .string()
                    .describe('Name of the required custom attribute'),
                description: z
                    .string()
                    .describe(
                        'Description of the custom attribute that needs to be created before the plan can be executed. Example: "The number of times a user has visited the website in the last 30 days."',
                    ),
            }),
        )
        .default([])
        .describe(
            'A list of custom attributes that need to be created before the audience plan can be executed. Only populated when planCreationStatus is PENDING.',
        ),
    planCreationStatus: z
        .nativeEnum(PlanCreationStatus)
        .default(PlanCreationStatus.SUCCESS),
    planCreationStatusDescription: z
        .string()
        .describe(
            'Human-readable one-line description of the plan creation status. Should explain what was accomplished or what is needed next in plain English.',
        ),
});

export type AudiencePlannerResponse = z.infer<
    typeof audiencePlannerResponseSchema
>;
